import React, { FormEvent, useEffect } from "react";
import { useSetAtom } from "jotai";
import { Controller, useForm } from "react-hook-form";
import { setModalCloseAtom } from "atoms/modalAtom/modalAtom";
import { useEmployeesState } from "atoms/employeesAtom";

import { NamedInput } from "components/NamedInput/NamedInput";
import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { ModalHeader } from "components/Modal/AppModal";
import { Button, EButtonVariant } from "components/Button/Button";

import styles from "../styles.module.scss";
import { FormAddEmployeesValues, AddEditEmployeeProps } from "../types";
import { useAddEmployee, useEditEmployee } from "../api";

export const AddEditEmployee = ({
  isEdit,
}: AddEditEmployeeProps): JSX.Element => {
  const closeModal = useSetAtom(setModalCloseAtom);
  const { employeesState } = useEmployeesState();

  const { mutate: addEmployee } = useAddEmployee();
  const { mutate: editEmployee } = useEditEmployee();

  const { handleSubmit, control, watch, setValue } =
    useForm<FormAddEmployeesValues>({
      defaultValues: {
        fullName: "",
        fullNameForCheck: "",
        employeeCode: "",
        cardNumber: "",
        password: "",
      },
    });

  const handleSave = async (data: FormAddEmployeesValues) => {
    const payload = {
      usersgroupid: employeesState.activeGroupItem?.item.usersgroupid || "",
      name: data.fullName,
      code: data.employeeCode,
      password: data.password,
      fiscalname: data.fullNameForCheck,
      card: data.cardNumber,
      usersid: "",
    };

    if (isEdit) {
      payload.usersid = employeesState.activeItem?.usersid || "";
      editEmployee(payload);
    } else {
      addEmployee(payload);
    }

    closeModal();
  };

  const canBeSaved = () => {
    return (
      watch("fullName") &&
      watch("fullNameForCheck") &&
      watch("employeeCode") &&
      watch("cardNumber") &&
      watch("password")
    );
  };

  useEffect(() => {
    if (isEdit) {
      setValue("fullName", employeesState.activeItem?.name || "");
      setValue("fullNameForCheck", employeesState.activeItem?.fiscalname || "");
      setValue("employeeCode", employeesState.activeItem?.code || "");
      setValue("cardNumber", employeesState.activeItem?.card || "");
      setValue("password", employeesState.activeItem?.password || "");
    }
  }, []);

  return (
    <BaseLayout
      className={styles.wrapper}
      header={
        <ModalHeader
          title={
            isEdit
              ? employeesState.activeItem?.name || ""
              : watch("fullName") || "Новый сотрудник"
          }
        />
      }
      footer={
        <Button
          onClick={handleSubmit(handleSave)}
          variant={EButtonVariant.SECONDARY}
          icon="galochka"
          disabled={!canBeSaved()}
        >
          Сохранить
        </Button>
      }
    >
      <form
        onSubmit={(e: FormEvent) => e.preventDefault()}
        className={styles.form}
      >
        <div className={styles.width_devider}>
          <Controller
            name="fullName"
            control={control}
            render={({ field }) => (
              <NamedInput
                {...field}
                isRequired
                label="ФИО"
                classNameContainer={styles.input_container}
              />
            )}
          />
          <Controller
            name="employeeCode"
            control={control}
            render={({ field }) => (
              <NamedInput
                {...field}
                isRequired
                type="number"
                label="Код сотрудника"
                classNameContainer={styles.input_container}
              />
            )}
          />
        </div>

        <div className={styles.width_devider}>
          <Controller
            name="fullNameForCheck"
            control={control}
            render={({ field }) => (
              <NamedInput
                {...field}
                isRequired
                label="ФИО для чека"
                classNameContainer={styles.input_container}
              />
            )}
          />
          <Controller
            name="cardNumber"
            control={control}
            render={({ field }) => (
              <NamedInput
                {...field}
                isRequired
                label="№ карты"
                type="number"
                classNameContainer={styles.input_container}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <NamedInput
                {...field}
                isRequired
                label="Пароль"
                type="password"
                classNameContainer={styles.input_container}
              />
            )}
          />
          {isEdit && (
            <NamedInput
              value={employeesState.activeItem?.usersid}
              disabled
              label="Код"
              type="number"
              classNameContainer={styles.input_container}
            />
          )}
        </div>
      </form>
    </BaseLayout>
  );
};
